import React, { useEffect, useMemo, useState } from "react";

const ImageGallery = ({ images = [], productName = "محصول" }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  const getImageSource = (img) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    if (typeof img === "object") {
      return img.url || img.image_url || img.image || "";
    }
    return "";
  };

  const imageUrls = useMemo(() => {
    return images.map((img) => {
      if (typeof File !== "undefined" && img instanceof File) {
        return URL.createObjectURL(img);
      }
      if (typeof Blob !== "undefined" && img instanceof Blob) {
        return URL.createObjectURL(img);
      }
      return getImageSource(img);
    });
  }, [images]);

  useEffect(() => {
    return () => {
      imageUrls.forEach((url, index) => {
        const img = images[index];
        if (
          (typeof File !== "undefined" && img instanceof File) ||
          (typeof Blob !== "undefined" && img instanceof Blob)
        ) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imageUrls, images]);

  useEffect(() => {
    if (!images.length) {
      setSelectedImageIndex(0);
      return;
    }

    const featuredIndex = images.findIndex(
      (img) => typeof img === "object" && img?.is_featured === true
    );

    setSelectedImageIndex(featuredIndex >= 0 ? featuredIndex : 0);
  }, [images]);

  const hasImages = imageUrls.length > 0;
  const hasMultipleImages = imageUrls.length > 1;
  const selectedImageUrl = imageUrls[selectedImageIndex];

  const goToImage = (index) => {
    if (!hasImages) return;
    const normalizedIndex = (index + imageUrls.length) % imageUrls.length;
    setSelectedImageIndex(normalizedIndex);
  };

  const handleNextImage = () => goToImage(selectedImageIndex + 1);
  const handlePrevImage = () => goToImage(selectedImageIndex - 1);

  const handleTouchStart = (event) => setTouchStartX(event.touches[0].clientX);
  const handleTouchEnd = (event) => {
    if (touchStartX === null || !hasMultipleImages) return;
    const deltaX = touchStartX - event.changedTouches[0].clientX;
    if (Math.abs(deltaX) > 40) {
      deltaX > 0 ? handleNextImage() : handlePrevImage();
    }
    setTouchStartX(null);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToImage(index);
    }
  };

  if (!hasImages) {
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-gray-100">
        <EmptyImageState />
      </div>
    );
  }

  return (
    <>
      <section dir="rtl" aria-label={`گالری تصاویر ${productName}`}>
        {/* تصویر اصلی */}
        <div
          className="group relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-lg shadow-gray-200/70 transition-all duration-300 hover:shadow-xl hover:shadow-gray-300/60"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl bg-gray-50 md:aspect-square">
            {selectedImageUrl ? (
              <img
                src={selectedImageUrl}
                alt={`${productName} - تصویر ${selectedImageIndex + 1}`}
                className="h-full w-full select-none object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                draggable={false}
              />
            ) : (
              <EmptyImageState />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />

            {hasMultipleImages && (
              <div className="absolute right-3 top-3 rounded-full bg-gray-900/65 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-sm">
                {selectedImageIndex + 1} / {imageUrls.length}
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsLightboxOpen(true)}
              className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#1E3A5F] shadow-md shadow-black/10 transition-all duration-200 hover:scale-105 hover:bg-white"
              aria-label="نمایش تصویر در حالت تمام‌صفحه"
            >
              <ExpandIcon />
            </button>

            {hasMultipleImages && (
              <>
                <button
                  type="button"
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1E3A5F] shadow-md shadow-black/10 transition-all duration-200 hover:scale-105 hover:bg-white"
                  aria-label="تصویر بعدی"
                >
                  <ChevronRightIcon />
                </button>
                <button
                  type="button"
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1E3A5F] shadow-md shadow-black/10 transition-all duration-200 hover:scale-105 hover:bg-white"
                  aria-label="تصویر قبلی"
                >
                  <ChevronLeftIcon />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Thumbnailها - 6 ستون در تمام اندازه‌ها */}
        {hasMultipleImages && (
          <div
            className="mt-2 grid grid-cols-6 gap-1 px-1 sm:gap-1.5 sm:px-2"
            aria-label="انتخاب تصویر"
          >
            {imageUrls.map((url, index) => {
              const isSelected = selectedImageIndex === index;

              return (
                <button
                  key={`${url}-${index}`}
                  type="button"
                  onClick={() => goToImage(index)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  className={`
                    relative aspect-square w-full overflow-hidden rounded-md bg-gray-100
                    transition-all duration-200
                    ${
                      isSelected
                        ? "ring-2 ring-[#1E3A5F] ring-offset-1 ring-offset-white"
                        : "opacity-90 hover:opacity-100 hover:ring-1 hover:ring-gray-300"
                    }
                  `}
                  aria-label={`انتخاب تصویر ${index + 1}`}
                  aria-current={isSelected ? "true" : undefined}
                >
                  {url ? (
                    <img
                      src={url}
                      alt={`${productName} - بندانگشتی ${index + 1}`}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <EmptyImageState small />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div
          dir="rtl"
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/90 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="نمایش بزرگ تصویر"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsLightboxOpen(false)}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#1E3A5F] shadow-lg shadow-black/20 transition-transform hover:scale-105"
            aria-label="بستن تصویر"
          >
            <CloseIcon />
          </button>

          <img
            src={selectedImageUrl}
            alt={`${productName} - تصویر بزرگ`}
            className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl shadow-black/30"
            onClick={(event) => event.stopPropagation()}
          />

          {hasMultipleImages && (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleNextImage();
                }}
                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#1E3A5F] shadow-lg shadow-black/20 transition-transform hover:scale-105"
                aria-label="تصویر بعدی"
              >
                <ChevronRightIcon />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handlePrevImage();
                }}
                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#1E3A5F] shadow-lg shadow-black/20 transition-transform hover:scale-105"
                aria-label="تصویر قبلی"
              >
                <ChevronLeftIcon />
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};

const EmptyImageState = ({ small = false }) => (
  <div className="flex h-full w-full items-center justify-center text-gray-300">
    <svg
      className={small ? "h-5 w-5" : "h-16 w-16"}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  </div>
);

const ChevronRightIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const ExpandIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 3H5a2 2 0 00-2 2v3m13-5h3a2 2 0 012 2v3M8 21H5a2 2 0 01-2-2v-3m13 5h3a2 2 0 002-2v-3"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default ImageGallery;
